import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ShippingOption {
  id: string;
  producer_id: string;
  code: string;
  label: string;
  description: string | null;
  type: 'pickup' | 'fixed';
  price_ore: number;
  currency: string;
  is_active: boolean;
  sort_order: number;
  // Shopify fields kept for DB compatibility but not used
  shopify_shipping_variant_id: string | null;
  shopify_shipping_product_id: string | null;
  shopify_sync_status: 'pending' | 'syncing' | 'synced' | 'error';
  shopify_last_error: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShippingOptionInsert {
  producer_id: string;
  code: string;
  label: string;
  description?: string | null;
  type: 'pickup' | 'fixed';
  price_ore: number;
  currency?: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface ShippingOptionUpdate {
  id: string;
  code?: string;
  label?: string;
  description?: string | null;
  type?: 'pickup' | 'fixed';
  price_ore?: number;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * Hook to fetch active shipping options for a producer (public/checkout use)
 */
export function useProducerShippingOptions(producerId: string | undefined) {
  return useQuery({
    queryKey: ['producer-shipping-options', producerId],
    queryFn: async () => {
      if (!producerId) return [];
      
      const { data, error } = await supabase
        .from('producer_shipping_options')
        .select('*')
        .eq('producer_id', producerId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('price_ore', { ascending: true });

      if (error) {
        console.error('Failed to fetch shipping options:', error);
        throw error;
      }

      return (data || []) as ShippingOption[];
    },
    enabled: !!producerId,
  });
}

/**
 * Hook to manage all shipping options for a producer (producer portal use)
 * Shopify sync is completely disabled - all changes are Farmly-only
 */
export function useProducerShippingOptionsAdmin(producerId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ['producer-shipping-options-admin', producerId],
    queryFn: async () => {
      if (!producerId) return [];
      
      const { data, error } = await supabase
        .from('producer_shipping_options')
        .select('*')
        .eq('producer_id', producerId)
        .order('sort_order', { ascending: true })
        .order('price_ore', { ascending: true });

      if (error) {
        console.error('Failed to fetch shipping options:', error);
        throw error;
      }

      return (data || []) as ShippingOption[];
    },
    enabled: !!producerId,
  });

  const createMutation = useMutation({
    mutationFn: async (option: ShippingOptionInsert) => {
      const { data, error } = await supabase
        .from('producer_shipping_options')
        .insert({
          ...option,          
        })
        .select()
        .single();

      if (error) throw error;
      return data as ShippingOption;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producer-shipping-options-admin', producerId] });
      queryClient.invalidateQueries({ queryKey: ['producer-shipping-options', producerId] });
      toast({ title: 'Fraktalternativ skapat' });
    },
    onError: (error: Error) => {
      console.error('Failed to create shipping option:', error);
      toast({
        title: 'Kunde inte skapa fraktalternativ',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: ShippingOptionUpdate) => {
      const { data, error } = await supabase
        .from('producer_shipping_options')
        .update({
          ...updates,
          // Keep synced status since we're not using Shopify
          shopify_sync_status: 'synced' as const,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as ShippingOption;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producer-shipping-options-admin', producerId] });
      queryClient.invalidateQueries({ queryKey: ['producer-shipping-options', producerId] });
      toast({ title: 'Fraktalternativ uppdaterat' });
    },
    onError: (error: Error) => {
      console.error('Failed to update shipping option:', error);
      toast({
        title: 'Kunde inte uppdatera fraktalternativ',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('producer_shipping_options')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producer-shipping-options-admin', producerId] });
      queryClient.invalidateQueries({ queryKey: ['producer-shipping-options', producerId] });
      toast({ title: 'Fraktalternativ borttaget' });
    },
    onError: (error: Error) => {
      console.error('Failed to delete shipping option:', error);
      toast({
        title: 'Kunde inte ta bort fraktalternativ',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    ...query,
    createOption: createMutation.mutate,
    updateOption: updateMutation.mutate,
    deleteOption: deleteMutation.mutate,
    // Sync is disabled - no-op function for backwards compatibility
    syncOption: (_id: string) => {
      console.log('[useProducerShippingOptionsAdmin] syncOption is disabled (Shopify off)');
    },
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    // Sync is always false since disabled
    isSyncing: false,
    syncingOptionId: null as string | null,
  };
}
